import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calculator, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const ComprehensiveProfitCalculator = () => {
  // Initial Setup Costs
  const [initialCosts, setInitialCosts] = useState({
    depositRent: 3300,
    sourcingFee: 1000,
    furnishing: 2500,
    other: 0
  });

  // Monthly Fixed Costs
  const [monthlyCosts, setMonthlyCosts] = useState({
    rent: 2500,
    councilTax: 150,
    water: 20,
    electricity: 20,
    gas: 0,
    broadband: 30,
    tvLicense: 0,
    cableTv: 0,
    insurance: 100,
    maintenance: 50
  });

  // Platform Fees & Commissions
  const [platformFees, setPlatformFees] = useState({
    airbnbCommission: 15, // percentage
    bookingCommission: 15, // percentage
    otherCommission: 0 // percentage
  });

  // Variable Costs
  const [variableCosts, setVariableCosts] = useState({
    cleaningPerStay: 50,
    linensPerStay: 15,
    consumablesPerStay: 10,
    guestAmenitiesPerStay: 5
  });

  // Income Parameters
  const [incomeParams, setIncomeParams] = useState({
    nightlyRate: 300,
    targetOccupancy: 80,
    avgStayLength: 3
  });

  // Calculated Results
  const [results, setResults] = useState(null);
  const [occupancyScenarios, setOccupancyScenarios] = useState([]);

  useEffect(() => {
    calculateResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCosts, monthlyCosts, platformFees, variableCosts, incomeParams]);

  const calculateResults = () => {
    // Calculate total initial setup cost
    const totalInitialCost = Object.values(initialCosts).reduce((a, b) => a + b, 0);

    // Calculate total monthly fixed costs
    const totalMonthlyFixed = Object.values(monthlyCosts).reduce((a, b) => a + b, 0);

    // Calculate average platform commission
    const avgCommission = (platformFees.airbnbCommission + platformFees.bookingCommission + platformFees.otherCommission) / 3;

    // Calculate variable cost per stay
    const totalVariableCostPerStay = Object.values(variableCosts).reduce((a, b) => a + b, 0);

    // Days in month
    const daysInMonth = 30;

    // Function to calculate for a given occupancy
    const calculateForOccupancy = (occupancyRate) => {
      const occupancyDecimal = occupancyRate / 100;
      const occupiedNights = daysInMonth * occupancyDecimal;
      const numberOfStays = Math.floor(occupiedNights / incomeParams.avgStayLength);

      // Gross rental income (before commission)
      const grossIncome = incomeParams.nightlyRate * occupiedNights;

      // Platform commission deduction
      const commissionAmount = grossIncome * (avgCommission / 100);
      const netRentalIncome = grossIncome - commissionAmount;

      // Variable costs for the month
      const totalVariableCosts = numberOfStays * totalVariableCostPerStay;

      // Total monthly costs
      const totalMonthlyCosts = totalMonthlyFixed + totalVariableCosts;

      // Monthly profit
      const monthlyProfit = netRentalIncome - totalMonthlyCosts;

      // Annual projections
      const annualGrossIncome = grossIncome * 12;
      const annualNetIncome = netRentalIncome * 12;
      const annualCosts = totalMonthlyCosts * 12;
      const annualProfit = monthlyProfit * 12;

      // ROI calculation (annual profit / initial investment)
      const roi = totalInitialCost > 0 ? (annualProfit / totalInitialCost) * 100 : 0;

      // Payback period in months
      const paybackMonths = monthlyProfit > 0 ? totalInitialCost / monthlyProfit : 0;

      return {
        occupancyRate,
        occupiedNights: Math.round(occupiedNights * 10) / 10,
        numberOfStays,
        grossIncome: Math.round(grossIncome),
        commissionAmount: Math.round(commissionAmount),
        netRentalIncome: Math.round(netRentalIncome),
        totalVariableCosts: Math.round(totalVariableCosts),
        totalMonthlyCosts: Math.round(totalMonthlyCosts),
        monthlyProfit: Math.round(monthlyProfit),
        annualGrossIncome: Math.round(annualGrossIncome),
        annualNetIncome: Math.round(annualNetIncome),
        annualCosts: Math.round(annualCosts),
        annualProfit: Math.round(annualProfit),
        roi: Math.round(roi * 10) / 10,
        paybackMonths: Math.round(paybackMonths * 10) / 10
      };
    };

    // Calculate for target occupancy
    const targetResults = calculateForOccupancy(incomeParams.targetOccupancy);

    // Calculate break-even occupancy
    let breakEvenOccupancy = 0;
    for (let occ = 10; occ <= 100; occ++) {
      const result = calculateForOccupancy(occ);
      if (result.monthlyProfit >= 0) {
        breakEvenOccupancy = occ;
        break;
      }
    }

    // Generate occupancy scenarios (100%, 95%, 90%, ..., down to break-even)
    const scenarios = [];
    for (let occ = 100; occ >= Math.max(breakEvenOccupancy - 10, 10); occ -= 5) {
      scenarios.push(calculateForOccupancy(occ));
    }

    setResults({
      ...targetResults,
      totalInitialCost,
      totalMonthlyFixed,
      avgCommission,
      breakEvenOccupancy,
      totalVariableCostPerStay
    });

    setOccupancyScenarios(scenarios);
  };

  const handleInitialCostChange = (key, value) => {
    setInitialCosts(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleMonthlyCostChange = (key, value) => {
    setMonthlyCosts(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handlePlatformFeeChange = (key, value) => {
    setPlatformFees(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleVariableCostChange = (key, value) => {
    setVariableCosts(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleIncomeParamChange = (key, value) => {
    setIncomeParams(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  if (!results) return null;

  return (
    <section id="calculator" className="py-20 md:py-32 bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/90">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Calculator className="w-12 h-12 text-white" />
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Professional Profit Calculator
              </h2>
            </div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Get a comprehensive breakdown of your short-term rental profitability including all costs, 
              platform commissions, and ROI projections.
            </p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <Tabs defaultValue="inputs" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="inputs">Input Your Numbers</TabsTrigger>
                  <TabsTrigger value="results">See Results & Analysis</TabsTrigger>
                </TabsList>

                {/* INPUTS TAB */}
                <TabsContent value="inputs" className="space-y-8">
                  {/* Income Parameters */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6 text-[#1F3A68] flex items-center gap-2">
                      <TrendingUp className="w-6 h-6" />
                      Income Parameters
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="nightlyRate" className="text-base font-semibold mb-2">
                          Nightly Rate (£)
                        </Label>
                        <Input
                          id="nightlyRate"
                          type="number"
                          value={incomeParams.nightlyRate}
                          onChange={(e) => handleIncomeParamChange('nightlyRate', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="targetOccupancy" className="text-base font-semibold mb-2">
                          Target Occupancy (%)
                        </Label>
                        <Input
                          id="targetOccupancy"
                          type="number"
                          min="0"
                          max="100"
                          value={incomeParams.targetOccupancy}
                          onChange={(e) => handleIncomeParamChange('targetOccupancy', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="avgStayLength" className="text-base font-semibold mb-2">
                          Avg. Stay Length (nights)
                        </Label>
                        <Input
                          id="avgStayLength"
                          type="number"
                          min="1"
                          value={incomeParams.avgStayLength}
                          onChange={(e) => handleIncomeParamChange('avgStayLength', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Initial Setup Costs */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6 text-[#1F3A68]">Initial Setup Costs</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <Label className="text-base font-semibold mb-2">Deposit/Rent (£)</Label>
                        <Input
                          type="number"
                          value={initialCosts.depositRent}
                          onChange={(e) => handleInitialCostChange('depositRent', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Sourcing Fee (£)</Label>
                        <Input
                          type="number"
                          value={initialCosts.sourcingFee}
                          onChange={(e) => handleInitialCostChange('sourcingFee', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Furnishing (£)</Label>
                        <Input
                          type="number"
                          value={initialCosts.furnishing}
                          onChange={(e) => handleInitialCostChange('furnishing', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Other Setup (£)</Label>
                        <Input
                          type="number"
                          value={initialCosts.other}
                          onChange={(e) => handleInitialCostChange('other', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Monthly Fixed Costs */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6 text-[#1F3A68]">Monthly Fixed Costs</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                      <div>
                        <Label className="text-base font-semibold mb-2">Rent/Mortgage (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.rent}
                          onChange={(e) => handleMonthlyCostChange('rent', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Council Tax (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.councilTax}
                          onChange={(e) => handleMonthlyCostChange('councilTax', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Water (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.water}
                          onChange={(e) => handleMonthlyCostChange('water', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Electricity (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.electricity}
                          onChange={(e) => handleMonthlyCostChange('electricity', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Gas (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.gas}
                          onChange={(e) => handleMonthlyCostChange('gas', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Broadband (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.broadband}
                          onChange={(e) => handleMonthlyCostChange('broadband', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">TV License (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.tvLicense}
                          onChange={(e) => handleMonthlyCostChange('tvLicense', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Cable/TV (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.cableTv}
                          onChange={(e) => handleMonthlyCostChange('cableTv', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Insurance (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.insurance}
                          onChange={(e) => handleMonthlyCostChange('insurance', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Maintenance (£)</Label>
                        <Input
                          type="number"
                          value={monthlyCosts.maintenance}
                          onChange={(e) => handleMonthlyCostChange('maintenance', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Platform Commissions */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6 text-[#1F3A68]">Platform Commissions</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <Label className="text-base font-semibold mb-2">
                          Airbnb Host Fee (%)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-4 h-4 inline ml-1 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Typically 3-15% depending on your plan</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={platformFees.airbnbCommission}
                          onChange={(e) => handlePlatformFeeChange('airbnbCommission', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">
                          Booking.com Fee (%)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Info className="w-4 h-4 inline ml-1 text-gray-400" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Typically 15-18%</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={platformFees.bookingCommission}
                          onChange={(e) => handlePlatformFeeChange('bookingCommission', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Other Platforms (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={platformFees.otherCommission}
                          onChange={(e) => handlePlatformFeeChange('otherCommission', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Variable Costs Per Stay */}
                  <div>
                    <h3 className="text-2xl font-bold mb-6 text-[#1F3A68]">Variable Costs Per Stay</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <Label className="text-base font-semibold mb-2">Cleaning (£)</Label>
                        <Input
                          type="number"
                          value={variableCosts.cleaningPerStay}
                          onChange={(e) => handleVariableCostChange('cleaningPerStay', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Linens (£)</Label>
                        <Input
                          type="number"
                          value={variableCosts.linensPerStay}
                          onChange={(e) => handleVariableCostChange('linensPerStay', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Consumables (£)</Label>
                        <Input
                          type="number"
                          value={variableCosts.consumablesPerStay}
                          onChange={(e) => handleVariableCostChange('consumablesPerStay', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                      <div>
                        <Label className="text-base font-semibold mb-2">Guest Amenities (£)</Label>
                        <Input
                          type="number"
                          value={variableCosts.guestAmenitiesPerStay}
                          onChange={(e) => handleVariableCostChange('guestAmenitiesPerStay', e.target.value)}
                          className="h-12 text-lg"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* RESULTS TAB */}
                <TabsContent value="results" className="space-y-8">
                  {/* Key Metrics Summary */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="bg-gradient-to-br from-[#1F3A68] to-[#1F3A68]/80 border-0">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-sm font-medium">Monthly Profit</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-3xl font-bold ${results.monthlyProfit >= 0 ? 'text-[#4ade80]' : 'text-red-400'}`}>
                          £{results.monthlyProfit.toLocaleString()}
                        </div>
                        <p className="text-white/60 text-sm mt-1">at {incomeParams.targetOccupancy}% occupancy</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-[#CB3B3B] to-[#CB3B3B]/80 border-0">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-sm font-medium">Annual Profit</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-3xl font-bold ${results.annualProfit >= 0 ? 'text-white' : 'text-red-200'}`}>
                          £{results.annualProfit.toLocaleString()}
                        </div>
                        <p className="text-white/60 text-sm mt-1">projected yearly</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-sm font-medium">ROI</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-white">
                          {results.roi}%
                        </div>
                        <p className="text-white/60 text-sm mt-1">annual return</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-amber-500 to-amber-600 border-0">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-sm font-medium">Break-Even</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-white">
                          {results.breakEvenOccupancy}%
                        </div>
                        <p className="text-white/60 text-sm mt-1">occupancy required</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Income Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          Income Breakdown (Monthly)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between items-center pb-3 border-b">
                          <span className="text-gray-600">Gross Rental Income</span>
                          <span className="font-bold text-lg">£{results.grossIncome.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b">
                          <span className="text-gray-600">Platform Commissions ({results.avgCommission.toFixed(1)}%)</span>
                          <span className="font-bold text-lg text-red-600">-£{results.commissionAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-gray-900 font-semibold">Net Rental Income</span>
                          <span className="font-bold text-xl text-[#1F3A68]">£{results.netRentalIncome.toLocaleString()}</span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Cost Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-[#1F3A68] flex items-center gap-2">
                          <TrendingDown className="w-5 h-5" />
                          Cost Breakdown (Monthly)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between items-center pb-3 border-b">
                          <span className="text-gray-600">Fixed Costs</span>
                          <span className="font-bold text-lg">£{results.totalMonthlyFixed.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b">
                          <span className="text-gray-600">Variable Costs ({results.numberOfStays} stays)</span>
                          <span className="font-bold text-lg">£{results.totalVariableCosts.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-gray-900 font-semibold">Total Monthly Costs</span>
                          <span className="font-bold text-xl text-[#CB3B3B]">£{results.totalMonthlyCosts.toLocaleString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Investment Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1F3A68]">Investment Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Initial Investment</div>
                          <div className="text-2xl font-bold text-[#1F3A68]">£{results.totalInitialCost.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Payback Period</div>
                          <div className="text-2xl font-bold text-[#1F3A68]">
                            {results.paybackMonths > 0 ? `${results.paybackMonths} months` : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Occupied Nights/Month</div>
                          <div className="text-2xl font-bold text-[#1F3A68]">{results.occupiedNights}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Occupancy Scenarios Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-[#1F3A68]">Occupancy Scenarios Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b-2 border-[#1F3A68]">
                              <th className="text-left py-3 px-4 font-semibold text-[#1F3A68]">Occupancy</th>
                              <th className="text-right py-3 px-4 font-semibold text-[#1F3A68]">Gross Income</th>
                              <th className="text-right py-3 px-4 font-semibold text-[#1F3A68]">Net Income</th>
                              <th className="text-right py-3 px-4 font-semibold text-[#1F3A68]">Total Costs</th>
                              <th className="text-right py-3 px-4 font-semibold text-[#1F3A68]">Monthly Profit</th>
                              <th className="text-center py-3 px-4 font-semibold text-[#1F3A68]">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {occupancyScenarios.map((scenario, index) => (
                              <tr 
                                key={index} 
                                className={`border-b ${
                                  scenario.occupancyRate === incomeParams.targetOccupancy 
                                    ? 'bg-blue-50 font-semibold' 
                                    : scenario.occupancyRate === results.breakEvenOccupancy
                                    ? 'bg-amber-50'
                                    : ''
                                }`}
                              >
                                <td className="py-3 px-4">{scenario.occupancyRate}%</td>
                                <td className="text-right py-3 px-4">£{scenario.grossIncome.toLocaleString()}</td>
                                <td className="text-right py-3 px-4">£{scenario.netRentalIncome.toLocaleString()}</td>
                                <td className="text-right py-3 px-4">£{scenario.totalMonthlyCosts.toLocaleString()}</td>
                                <td className={`text-right py-3 px-4 font-bold ${
                                  scenario.monthlyProfit >= 0 ? 'text-[#059669]' : 'text-[#CB3B3B]'
                                }`}>
                                  £{scenario.monthlyProfit.toLocaleString()}
                                </td>
                                <td className="text-center py-3 px-4">
                                  {scenario.monthlyProfit > 0 ? (
                                    <TrendingUp className="w-5 h-5 text-green-600 inline" />
                                  ) : scenario.monthlyProfit < 0 ? (
                                    <TrendingDown className="w-5 h-5 text-red-600 inline" />
                                  ) : (
                                    <Minus className="w-5 h-5 text-gray-400 inline" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 flex gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-100 border border-blue-200"></div>
                          <span>Your Target Occupancy</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-amber-100 border border-amber-200"></div>
                          <span>Break-Even Point</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ComprehensiveProfitCalculator;
